import { z } from 'zod';
import { MetaSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared BFF↔SPA response contracts for the Drop-6 workstream Files API
 * (ADR-0045). The BFF's artifact routes invoke the `workstreamagent`
 * (`listArtifacts` / `getArtifact` / `deleteArtifact`, `workstream-agent.ts`),
 * narrow the agent's tolerant-out reply into these shapes, and return them in
 * the standard `{ data, meta }` envelope. The SPA's query/mutation hooks parse
 * against these.
 *
 * `data` schemas are **strict** (the BFF authors them; a stray key is a bug);
 * the outer envelopes are **loose** (`z.looseObject`, ADR-0024 §11 — a future
 * additive field never breaks an old client).
 */

/**
 * One file's metadata, as listed in the Files panel. `artifactId` is the OPAQUE
 * per-artifact id (the uuid segment of the S3 key, ADR-0046) — the SPA uses it as
 * the `{artifactId}` path segment for view/delete; the durable S3 key stays
 * server-side. `lastModified` is the S3 object's timestamp — there is no
 * per-artifact date on the ledger itself. No `url`/`preview` here: the list is
 * metadata-only; a fresh presigned URL is fetched on demand by `getArtifact`.
 */
export const ArtifactListItemSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  lastModified: Rfc3339DateTime,
});
export type ArtifactListItem = z.infer<typeof ArtifactListItemSchema>;

/** `data` of `GET /api/v1/workstreams/{id}/artifacts`. */
export const ListArtifactsDataSchema = z.strictObject({
  artifacts: z.array(ArtifactListItemSchema),
});
export type ListArtifactsData = z.infer<typeof ListArtifactsDataSchema>;

export const ListArtifactsResponseSchema = z.looseObject({
  data: ListArtifactsDataSchema,
  meta: MetaSchema,
});
export type ListArtifactsResponse = z.infer<typeof ListArtifactsResponseSchema>;

/**
 * `data` of `GET /api/v1/workstreams/{id}/artifacts/{artifactId}` — one file
 * fetched for viewing. `content` carries the full text for text-family types
 * (rendered inline: HTML in the sandboxed iframe, markdown/code); `url` is a
 * freshly-presigned GET for download / binary. `null` from the agent normalises
 * to `undefined`.
 */
export const GetArtifactDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  content: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
  // A presigned S3 GET is always https; pin the scheme so a buggy/compromised
  // agent can't smuggle a `javascript:` / `data:` / `file:` URL into a value the
  // SPA navigates to (defence-in-depth — the SPA consumer also handles links safely).
  url: z
    .url({ protocol: /^https$/ })
    .nullish()
    .transform((v) => v ?? undefined),
});
export type GetArtifactData = z.infer<typeof GetArtifactDataSchema>;

export const GetArtifactResponseSchema = z.looseObject({
  data: GetArtifactDataSchema,
  meta: MetaSchema,
});
export type GetArtifactResponse = z.infer<typeof GetArtifactResponseSchema>;

/** `data` of `DELETE /api/v1/workstreams/{id}/artifacts/{artifactId}` — the deleted id (idempotent). */
export const DeleteArtifactDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
});
export type DeleteArtifactData = z.infer<typeof DeleteArtifactDataSchema>;

export const DeleteArtifactResponseSchema = z.looseObject({
  data: DeleteArtifactDataSchema,
  meta: MetaSchema,
});
export type DeleteArtifactResponse = z.infer<typeof DeleteArtifactResponseSchema>;

/**
 * Drop 8 (Files rail upload) — the single source of truth for uploadable file
 * types, mirrored in Python (`contracts/artifacts.py`) and asserted equal by
 * each side's own test suite. Adding a type is a one-line change here (+ the
 * Python mirror) — `UPLOAD_ACCEPT` and the content-type union below both
 * derive from this, so nothing else needs touching.
 *
 * `magic` is the byte signature Phase E's `verify_uploaded_object` checks
 * (ranged `GET bytes=0-7`); `null` means no magic bytes exist for the type —
 * Markdown is verified as valid UTF-8 instead.
 */
export const UPLOAD_TYPES = {
  'text/markdown': { extensions: ['.md', '.markdown'], label: 'Markdown', magic: null },
  'application/pdf': { extensions: ['.pdf'], label: 'PDF', magic: '%PDF-' },
} as const;
export type UploadContentType = keyof typeof UPLOAD_TYPES;

/** `<input accept>` value: every extension, then every MIME type — e.g. `.md,.markdown,.pdf,text/markdown,application/pdf`. */
export const UPLOAD_ACCEPT = [
  ...Object.values(UPLOAD_TYPES).flatMap((t) => t.extensions as readonly string[]),
  ...Object.keys(UPLOAD_TYPES),
].join(',');

/** 25 MB — enforced by the presigned POST policy's `content-length-range` (Phase D), S3 is the sole size gate. */
export const MAX_UPLOAD_BYTES = 26_214_400;

/**
 * Ledger cap per workstream. `WorkstreamState` is a single 400 KB DynamoDB
 * item (ADR-0033/ADR-0040) and, per ADR-0049, uploaded artifacts never age
 * out of it — this cap is now the only thing bounding unbounded growth.
 * Enforced by Phase E's `finalize_upload` (409 once reached).
 */
export const MAX_UPLOAD_ARTIFACTS = 100;

const UPLOAD_CONTENT_TYPE_KEYS = Object.keys(UPLOAD_TYPES) as [
  UploadContentType,
  ...UploadContentType[],
];

/**
 * `data` of `POST /api/v1/workstreams/{id}/artifacts` (Phase 1 — begin, Phase F).
 * `contentType` is declared by the SPA from the filename's extension via
 * `UPLOAD_TYPES` — **never** `File.type` (browsers report `""` or `text/plain`
 * for `.md`). The BFF 415s an unlisted type before any URL is signed.
 */
export const BeginUploadRequestSchema = z.strictObject({
  filename: z.string().min(1).max(255),
  contentType: z.enum(UPLOAD_CONTENT_TYPE_KEYS),
});
export type BeginUploadRequest = z.infer<typeof BeginUploadRequestSchema>;

/**
 * `data` of `POST /api/v1/workstreams/{id}/artifacts/{artifactId}/complete`
 * (Phase 3 — finalize, Phase F). `{artifactId}` is a URL path segment, not a
 * body field. The body carries `filename` only — `contentType`/`sizeBytes`
 * are trusted from S3's `HeadObject`, never from the client (design doc,
 * "Finalize trusts S3, not the client").
 */
export const FinalizeUploadRequestSchema = z.strictObject({
  filename: z.string().min(1).max(255),
});
export type FinalizeUploadRequest = z.infer<typeof FinalizeUploadRequestSchema>;

/**
 * `data` of `POST /api/v1/workstreams/{id}/artifacts` on success (Phase 1 —
 * begin, Phase F). `fields` is the **opaque** presigned-POST form the SPA
 * replays verbatim into a `FormData` for the direct-to-S3 upload (Phase 2) —
 * every key/value the agent signed (`Content-Type`, `tagging`, `key`,
 * `policy`, `signature`, …), never inspected or reshaped here.
 */
export const BeginUploadDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  url: z.url({ protocol: /^https$/ }),
  fields: z.record(z.string(), z.string()),
});
export type BeginUploadData = z.infer<typeof BeginUploadDataSchema>;

export const BeginUploadResponseSchema = z.looseObject({
  data: BeginUploadDataSchema,
  meta: MetaSchema,
});
export type BeginUploadResponse = z.infer<typeof BeginUploadResponseSchema>;

/**
 * `data` of `POST /api/v1/workstreams/{id}/artifacts/{artifactId}/complete`
 * on success (`committed: true`, Phase 3 — finalize, Phase F). Mirrors
 * `ArtifactListItemSchema` minus `lastModified` — finalize doesn't re-HEAD
 * after committing, it already has these fields from its own verification.
 */
export const FinalizeUploadDataSchema = z.strictObject({
  artifactId: z.string().min(1).max(128),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
});
export type FinalizeUploadData = z.infer<typeof FinalizeUploadDataSchema>;

export const FinalizeUploadResponseSchema = z.looseObject({
  data: FinalizeUploadDataSchema,
  meta: MetaSchema,
});
export type FinalizeUploadResponse = z.infer<typeof FinalizeUploadResponseSchema>;
